import React, { useState, useEffect } from 'react';
import { X, User, Mail, Phone, Users, Plane, Check, CreditCard, Info } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

// Configuration des cartes et leurs avantages
const CARD_LIMITS = {
  'Business': { maxMemberSeats: 1, label: 'Business' },
  'Magellan': { maxMemberSeats: 2, label: 'Magellan' },
  'Orlamonde': { maxMemberSeats: 5, label: 'Orlamonde' },
  'Forfait': { maxMemberSeats: 1, label: 'Forfait' }
};

const BookingModal = ({ isOpen, onClose, item, bookingType, onSuccess }) => {
  const { language } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [memberInfo, setMemberInfo] = useState(null);
  const [loadingMember, setLoadingMember] = useState(false);
  
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    nb_persons_member: 1,
    nb_persons_non_member: 0,
    with_flight: false,
    member_card_number: localStorage.getItem('memberCardNumber') || '',
    notes: ''
  });

  // Récupérer les infos du membre quand le numéro de carte change
  useEffect(() => {
    const fetchMemberInfo = async () => {
      const cardNumber = formData.member_card_number?.replace(/\s/g, '');
      if (!cardNumber || cardNumber.length < 16) {
        setMemberInfo(null);
        return;
      }

      setLoadingMember(true);
      try {
        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/member/info/${cardNumber}`
        );
        if (response.ok) {
          const data = await response.json();
          setMemberInfo(data);
          // Pré-remplir les infos si disponibles
          if (data.first_name) {
            setFormData(prev => ({
              ...prev,
              first_name: data.first_name || prev.first_name,
              last_name: data.last_name || prev.last_name,
              email: data.email || prev.email,
              phone: data.phone || prev.phone
            }));
          }
        } else {
          setMemberInfo(null);
        }
      } catch (err) {
        console.error('Error fetching member info:', err);
        setMemberInfo(null);
      } finally {
        setLoadingMember(false);
      }
    };

    const debounceTimer = setTimeout(fetchMemberInfo, 500);
    return () => clearTimeout(debounceTimer);
  }, [formData.member_card_number]);

  if (!isOpen || !item) return null;

  const availableSeats = (item.total_seats || 0) - (item.seats_sold || 0);
  const isStay = bookingType === 'stay';

  // Déterminer le nombre max de places au tarif membre selon la carte
  const cardType = memberInfo?.level || null;
  const cardConfig = cardType ? CARD_LIMITS[cardType] : null;
  const maxMemberSeats = cardConfig ? cardConfig.maxMemberSeats : 0;
  const isMember = !!memberInfo;

  // Calculer le prix
  const calculatePrice = () => {
    let memberUnitPrice = 0;
    let nonMemberUnitPrice = 0;
    
    if (isStay) {
      memberUnitPrice = formData.with_flight 
        ? (item.price_member_with_flight || 0) 
        : (item.price_member_no_flight || 0);
      nonMemberUnitPrice = formData.with_flight 
        ? (item.price_non_member_with_flight || 0) 
        : (item.price_non_member_no_flight || 0);
    } else {
      memberUnitPrice = item.price_member || 0;
      nonMemberUnitPrice = item.price_non_member || 0;
    }

    const nbMember = isMember ? parseInt(formData.nb_persons_member) || 0 : 0;
    const nbNonMember = parseInt(formData.nb_persons_non_member) || 0;
    
    // Si pas membre, tout est au tarif non-membre
    if (!isMember) {
      return {
        memberUnitPrice: 0,
        nonMemberUnitPrice,
        memberSeats: 0,
        nonMemberSeats: nbNonMember,
        memberTotal: 0,
        nonMemberTotal: nonMemberUnitPrice * nbNonMember,
        totalPrice: nonMemberUnitPrice * nbNonMember,
        totalSeats: nbNonMember
      };
    }
    
    return {
      memberUnitPrice,
      nonMemberUnitPrice,
      memberSeats: nbMember,
      nonMemberSeats: nbNonMember,
      memberTotal: memberUnitPrice * nbMember,
      nonMemberTotal: nonMemberUnitPrice * nbNonMember,
      totalPrice: (memberUnitPrice * nbMember) + (nonMemberUnitPrice * nbNonMember),
      totalSeats: nbMember + nbNonMember
    };
  };

  const pricing = calculatePrice();

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    if (pricing.totalSeats === 0) {
      setError(language === 'fr' ? 'Veuillez sélectionner au moins une place' : 'Please select at least one seat');
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/member/bookings`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          booking_type: bookingType,
          item_id: item.id,
          first_name: formData.first_name,
          last_name: formData.last_name,
          email: formData.email,
          phone: formData.phone,
          nb_persons: pricing.totalSeats,
          nb_persons_member: pricing.memberSeats,
          nb_persons_non_member: pricing.nonMemberSeats,
          total_price: pricing.totalPrice,
          options: {
            with_flight: formData.with_flight,
            member_card_number: formData.member_card_number || null,
            member_level: cardType
          },
          notes: formData.notes
        })
      });

      if (response.ok) {
        const data = await response.json();
        setSuccess(true);
        if (onSuccess) onSuccess(data);
      } else {
        const errorData = await response.json();
        setError(errorData.detail || (language === 'fr' ? 'Erreur lors de la réservation' : 'Booking error'));
      }
    } catch (err) {
      setError(language === 'fr' ? 'Erreur de connexion' : 'Connection error');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setSuccess(false);
    setError('');
    setMemberInfo(null);
    setFormData({
      first_name: '',
      last_name: '',
      email: '',
      phone: '',
      nb_persons_member: 1,
      nb_persons_non_member: 0,
      with_flight: false,
      member_card_number: localStorage.getItem('memberCardNumber') || '',
      notes: ''
    });
    onClose();
  };

  // Success screen
  if (success) {
    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0,0,0,0.7)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: '20px'
      }}>
        <div style={{
          background: 'var(--bg-primary)',
          maxWidth: '500px',
          width: '100%',
          padding: '48px 32px',
          textAlign: 'center'
        }}>
          <div style={{
            width: '80px',
            height: '80px',
            borderRadius: '50%',
            background: '#2c5f7d',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 24px'
          }}>
            <Check size={40} color="#fff" />
          </div>
          <h2 className="hero-medium" style={{ marginBottom: '16px' }}>
            {language === 'fr' ? 'Réservation confirmée !' : 'Booking confirmed!'}
          </h2>
          <p className="body-regular" style={{ color: 'var(--text-secondary)', marginBottom: '24px' }}>
            {language === 'fr' 
              ? 'Votre demande de réservation a été enregistrée. Nous vous contacterons prochainement pour confirmer votre participation.'
              : 'Your booking request has been registered. We will contact you soon to confirm your participation.'}
          </p>
          <p className="body-regular" style={{ marginBottom: '32px' }}>
            <strong>{language === 'fr' ? 'Total' : 'Total'}: {pricing.totalPrice.toLocaleString('fr-FR')} €</strong>
          </p>
          <button onClick={handleClose} className="btn-primary">
            {language === 'fr' ? 'Fermer' : 'Close'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0,0,0,0.7)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '20px',
      overflowY: 'auto'
    }}>
      <div style={{
        background: 'var(--bg-primary)',
        maxWidth: '600px',
        width: '100%',
        maxHeight: '90vh',
        overflowY: 'auto'
      }}>
        {/* Header */}
        <div style={{
          padding: '24px 32px',
          borderBottom: '1px solid var(--border-light)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          position: 'sticky',
          top: 0,
          background: 'var(--bg-primary)',
          zIndex: 1
        }}>
          <h2 className="heading-1">
            {language === 'fr' ? 'Réserver' : 'Book'}
          </h2>
          <button
            onClick={handleClose}
            style={{
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              padding: '8px'
            }}
          >
            <X size={24} color="var(--text-secondary)" />
          </button>
        </div>

        {/* Item Info */}
        <div style={{
          padding: '24px 32px',
          background: 'var(--bg-secondary)',
          borderBottom: '1px solid var(--border-light)'
        }}>
          <h3 className="heading-2" style={{ marginBottom: '8px' }}>
            {language === 'fr' ? item.title : (item.title_en || item.title)}
          </h3>
          <p className="body-regular" style={{ color: 'var(--text-secondary)' }}>
            {item.date} • {item.location}
          </p>
          <p className="body-small" style={{ color: '#2c5f7d', marginTop: '8px' }}>
            {availableSeats} {language === 'fr' ? 'places disponibles' : 'seats available'}
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} style={{ padding: '32px' }}>
          {error && (
            <div style={{
              padding: '12px 16px',
              background: '#fee',
              border: '1px solid #c41e3a',
              marginBottom: '24px',
              color: '#c41e3a'
            }}>
              {error}
            </div>
          )}

          {/* Member Card */}
          <div style={{ marginBottom: '20px' }}>
            <label className="body-regular" style={{ display: 'block', marginBottom: '8px', fontWeight: 600 }}>
              <CreditCard size={16} style={{ display: 'inline', marginRight: '8px' }} />
              {language === 'fr' ? 'N° Carte Membre' : 'Member Card #'}
            </label>
            <input
              type="text"
              name="member_card_number"
              value={formData.member_card_number}
              onChange={handleChange}
              placeholder={language === 'fr' ? 'Votre numéro de carte membre' : 'Your member card number'}
              style={{
                width: '100%',
                padding: '12px',
                border: '1px solid var(--border-light)',
                fontSize: '14px',
                fontFamily: 'inherit'
              }}
            />
            
            {/* Card Status */}
            {loadingMember && (
              <p className="body-small" style={{ marginTop: '8px', color: 'var(--text-secondary)' }}>
                {language === 'fr' ? 'Vérification...' : 'Verifying...'}
              </p>
            )}
            
            {memberInfo && (
              <div style={{
                marginTop: '12px',
                padding: '12px 16px',
                background: 'rgba(44, 95, 125, 0.1)',
                border: '1px solid #2c5f7d',
                borderRadius: '4px'
              }}>
                <p className="body-small" style={{ fontWeight: 600, color: '#2c5f7d', marginBottom: '4px' }}>
                  ✓ {language === 'fr' ? 'Carte' : 'Card'} {cardType} {language === 'fr' ? 'reconnue' : 'recognized'}
                </p>
                <p className="body-small" style={{ color: 'var(--text-secondary)' }}>
                  {language === 'fr' 
                    ? `Vous pouvez réserver jusqu'à ${maxMemberSeats} place(s) au tarif membre`
                    : `You can book up to ${maxMemberSeats} seat(s) at member rate`}
                </p>
              </div>
            )}
            
            {!memberInfo && formData.member_card_number && !loadingMember && (
              <p className="body-small" style={{ marginTop: '8px', color: 'var(--text-secondary)' }}>
                {language === 'fr' 
                  ? 'Entrez un numéro de carte valide pour bénéficier du tarif membre'
                  : 'Enter a valid card number to get the member rate'}
              </p>
            )}
          </div>

          {/* Name */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '20px' }}>
            <div>
              <label className="body-regular" style={{ display: 'block', marginBottom: '8px', fontWeight: 600 }}>
                {language === 'fr' ? 'Prénom *' : 'First Name *'}
              </label>
              <input
                type="text"
                name="first_name"
                value={formData.first_name}
                onChange={handleChange}
                required
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '1px solid var(--border-light)',
                  fontSize: '14px',
                  fontFamily: 'inherit'
                }}
              />
            </div>
            <div>
              <label className="body-regular" style={{ display: 'block', marginBottom: '8px', fontWeight: 600 }}>
                {language === 'fr' ? 'Nom *' : 'Last Name *'}
              </label>
              <input
                type="text"
                name="last_name"
                value={formData.last_name}
                onChange={handleChange}
                required
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '1px solid var(--border-light)',
                  fontSize: '14px',
                  fontFamily: 'inherit'
                }}
              />
            </div>
          </div>

          {/* Email & Phone */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '20px' }}>
            <div>
              <label className="body-regular" style={{ display: 'block', marginBottom: '8px', fontWeight: 600 }}>
                Email *
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '1px solid var(--border-light)',
                  fontSize: '14px',
                  fontFamily: 'inherit'
                }}
              />
            </div>
            <div>
              <label className="body-regular" style={{ display: 'block', marginBottom: '8px', fontWeight: 600 }}>
                {language === 'fr' ? 'Téléphone *' : 'Phone *'}
              </label>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                required
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '1px solid var(--border-light)',
                  fontSize: '14px',
                  fontFamily: 'inherit'
                }}
              />
            </div>
          </div>

          {/* Number of seats - Member section */}
          {isMember && maxMemberSeats > 0 && (
            <div style={{ marginBottom: '20px' }}>
              <label className="body-regular" style={{ display: 'block', marginBottom: '8px', fontWeight: 600 }}>
                <Users size={16} style={{ display: 'inline', marginRight: '8px' }} />
                {language === 'fr' ? `Places au tarif membre (max ${maxMemberSeats})` : `Seats at member rate (max ${maxMemberSeats})`}
              </label>
              <select
                name="nb_persons_member"
                value={formData.nb_persons_member}
                onChange={handleChange}
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '1px solid var(--border-light)',
                  fontSize: '14px',
                  fontFamily: 'inherit',
                  background: 'var(--bg-primary)'
                }}
              >
                {[...Array(Math.min(maxMemberSeats, availableSeats) + 1)].map((_, i) => (
                  <option key={i} value={i}>
                    {i} {language === 'fr' ? (i <= 1 ? 'place' : 'places') : (i <= 1 ? 'seat' : 'seats')}
                  </option>
                ))}
              </select>
              <p className="body-small" style={{ marginTop: '4px', color: '#2c5f7d' }}>
                {language === 'fr' 
                  ? `Prix membre : ${isStay ? (formData.with_flight ? item.price_member_with_flight : item.price_member_no_flight) : item.price_member || 0} € / pers.`
                  : `Member price: ${isStay ? (formData.with_flight ? item.price_member_with_flight : item.price_member_no_flight) : item.price_member || 0} € / person`}
              </p>
            </div>
          )}

          {/* Number of seats - Non-member / Additional guests */}
          <div style={{ marginBottom: '20px' }}>
            <label className="body-regular" style={{ display: 'block', marginBottom: '8px', fontWeight: 600 }}>
              <Users size={16} style={{ display: 'inline', marginRight: '8px' }} />
              {isMember 
                ? (language === 'fr' ? 'Places supplémentaires (tarif non-membre)' : 'Additional seats (non-member rate)')
                : (language === 'fr' ? 'Nombre de places *' : 'Number of seats *')
              }
            </label>
            <select
              name="nb_persons_non_member"
              value={formData.nb_persons_non_member}
              onChange={handleChange}
              required={!isMember}
              style={{
                width: '100%',
                padding: '12px',
                border: '1px solid var(--border-light)',
                fontSize: '14px',
                fontFamily: 'inherit',
                background: 'var(--bg-primary)'
              }}
            >
              {[...Array(Math.min(availableSeats - (isMember ? parseInt(formData.nb_persons_member) || 0 : 0), 10) + 1)].map((_, i) => (
                <option key={i} value={i}>
                  {i} {language === 'fr' ? (i <= 1 ? 'place' : 'places') : (i <= 1 ? 'seat' : 'seats')}
                </option>
              ))}
            </select>
            <p className="body-small" style={{ marginTop: '4px', color: 'var(--text-secondary)' }}>
              {language === 'fr' 
                ? `Prix non-membre : ${isStay ? (formData.with_flight ? item.price_non_member_with_flight : item.price_non_member_no_flight) : item.price_non_member || 0} € / pers.`
                : `Non-member price: ${isStay ? (formData.with_flight ? item.price_non_member_with_flight : item.price_non_member_no_flight) : item.price_non_member || 0} € / person`}
            </p>
          </div>

          {/* With/Without flight (for stays only) */}
          {isStay && (
            <div style={{ marginBottom: '20px' }}>
              <label style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                cursor: 'pointer',
                padding: '16px',
                border: formData.with_flight ? '2px solid #2c5f7d' : '1px solid var(--border-light)',
                background: formData.with_flight ? 'rgba(44, 95, 125, 0.05)' : 'transparent'
              }}>
                <input
                  type="checkbox"
                  name="with_flight"
                  checked={formData.with_flight}
                  onChange={handleChange}
                  style={{ width: '20px', height: '20px' }}
                />
                <Plane size={20} color="#2c5f7d" />
                <span className="body-regular">
                  {language === 'fr' ? 'Avec transport en jet privé' : 'With private jet transport'}
                </span>
              </label>
            </div>
          )}

          {/* Notes */}
          <div style={{ marginBottom: '24px' }}>
            <label className="body-regular" style={{ display: 'block', marginBottom: '8px', fontWeight: 600 }}>
              {language === 'fr' ? 'Remarques (optionnel)' : 'Comments (optional)'}
            </label>
            <textarea
              name="notes"
              value={formData.notes}
              onChange={handleChange}
              rows={3}
              placeholder={language === 'fr' ? 'Régime alimentaire, demandes spéciales...' : 'Dietary requirements, special requests...'}
              style={{
                width: '100%',
                padding: '12px',
                border: '1px solid var(--border-light)',
                fontSize: '14px',
                fontFamily: 'inherit',
                resize: 'vertical'
              }}
            />
          </div>

          {/* Price Summary */}
          <div style={{
            padding: '20px',
            background: 'var(--bg-secondary)',
            border: '1px solid var(--border-light)',
            marginBottom: '24px'
          }}>
            {/* Member seats */}
            {pricing.memberSeats > 0 && (
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                <span className="body-regular" style={{ color: 'var(--text-secondary)' }}>
                  {pricing.memberSeats} × {language === 'fr' ? 'tarif membre' : 'member rate'}
                </span>
                <span className="body-regular">{pricing.memberTotal.toLocaleString('fr-FR')} €</span>
              </div>
            )}
            
            {/* Non-member seats */}
            {pricing.nonMemberSeats > 0 && (
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                <span className="body-regular" style={{ color: 'var(--text-secondary)' }}>
                  {pricing.nonMemberSeats} × {language === 'fr' ? 'tarif non-membre' : 'non-member rate'}
                </span>
                <span className="body-regular">{pricing.nonMemberTotal.toLocaleString('fr-FR')} €</span>
              </div>
            )}
            
            {/* Total seats */}
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
              <span className="body-regular" style={{ color: 'var(--text-secondary)' }}>
                {language === 'fr' ? 'Nombre total de places' : 'Total seats'}
              </span>
              <span className="body-regular">{pricing.totalSeats}</span>
            </div>
            
            {/* Total */}
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              paddingTop: '12px',
              borderTop: '1px solid var(--border-light)'
            }}>
              <span className="heading-2">Total</span>
              <span className="heading-2" style={{ color: '#2c5f7d' }}>
                {pricing.totalPrice.toLocaleString('fr-FR')} €
              </span>
            </div>
          </div>

          {/* Submit */}
          <button
            type="submit"
            disabled={loading || availableSeats === 0 || pricing.totalSeats === 0}
            className="btn-primary"
            style={{
              width: '100%',
              justifyContent: 'center',
              opacity: (loading || pricing.totalSeats === 0) ? 0.6 : 1
            }}
          >
            {loading 
              ? (language === 'fr' ? 'Envoi en cours...' : 'Sending...')
              : (language === 'fr' ? 'Confirmer la réservation' : 'Confirm booking')
            }
          </button>

          <p className="body-small" style={{ marginTop: '16px', textAlign: 'center', color: 'var(--text-secondary)' }}>
            {language === 'fr' 
              ? 'En confirmant, vous serez contacté pour finaliser votre réservation.'
              : 'By confirming, you will be contacted to finalize your booking.'}
          </p>
        </form>
      </div>
    </div>
  );
};

export default BookingModal;
